import {
  apiGetTrades,
  apiGetInsiders,
  apiGetNextPage,
  apiSearchTrades,
  apiGetTradesBookmarks,
  apiToggleTradeBookmark,
} from "@/Groups/Polymarket/private/api/polymarketApi";

import { getInitialCategories, saveCategories, getInitialBookmarksCount } from "@/Groups/Polymarket/private/composables/useLocalStorage";
import {
  CATEGORIES_STORAGE_KEY,
  DEFAULT_CATEGORIES,
  INSIDER_FILTER_PARAMS,
  INSIDER_EXCLUDED_CATEGORY_SLUGS,
  INSIDER_PARAM_NAMES,
} from "@/Groups/Polymarket/private/constants/config";

// null = «использовать бэкендный дефолт». В URL/API пустые значения не шлём.
const emptyInsiderParams = () => INSIDER_PARAM_NAMES.reduce((acc, name) => { acc[name] = null; return acc; }, {});
import { mapInsidersToTrades } from "@/Groups/Polymarket/store/helpers/mapInsidersToTrades";

//Загружает первую страницу списка: трейды или инсайдеров (в зависимости от активного фильтра).
//Унифицирует форму ответа под { results, next } для использования во всех экшенах.
const fetchTradesList = async (params, signal = null) => {
  if (params.insider_mode) {
    const data = await apiGetInsiders({
      page: params.page || 1,
      freshWallet: params.fresh_wallet,
      activeHold: params.active_hold,
      category: params.category,
      insiderParams: params.insider_params,
      signal,
    });
    return { results: mapInsidersToTrades(data.results), next: data.next };
  }
  const data = await apiGetTrades(params, signal);
  return { results: data.results, next: data.next };
};
import { sortMapByFilter } from "@/Groups/Polymarket/store/helpers/sortMapByFilter";
import { buildTradesUrl } from "@/Groups/Polymarket/store/helpers/buildTradesUrl";
import { updateBookmarksCount } from "@/Groups/Polymarket/store/helpers/updateBookmarksCount";

const Polymarket = {
  namespaced: true,

  state: {
    data: {},
    map: [],
    bookmarksMap: [],
    bookmarksCount: getInitialBookmarksCount(),
    next: null,
    isReady: false,
    isLoadingNext: false,
    isLoadingFilter: false,
    isBookmarksLoaded: false,
    
    filters: {
      top: null,
      topValue: null,
      additional: null,
      categories: getInitialCategories(),
      freshWallet: true,
      activeHold: false,
      insiderParams: emptyInsiderParams(),
    },
    
    searchQuery: '',
    currentTradesUrl: null,
    
    requestVersion: 0,
    abortController: null,
  },

  actions: {
    //======================================================================================
    // ИНИЦИАЛИЗАЦИЯ
    //======================================================================================
    //Инициализация приложения: загружает фильтры из URL, избранное и первую страницу трейдов
    async init({ commit, dispatch, getters }) {
      await dispatch('initFiltersFromUrl');
      await dispatch('loadBookmarks');
      const params = getters.activeFiltersParams;
      const { results, next } = await fetchTradesList(params);
      const url = buildTradesUrl(params);
      commit('SET_DATA', results);
      commit('SET_NEXT', next);
      commit('SET_CURRENT_TRADES_URL', url);
      commit('SET_READY_FLAG', true);
    },

    //Инициализация фильтров из URL-параметров при первой загрузке страницы
    async initFiltersFromUrl({ commit }) {
      const urlParams = new URLSearchParams(window.location.search);
      const insiderUrlValue = urlParams.get('insider');
      if (insiderUrlValue && INSIDER_FILTER_PARAMS.includes(insiderUrlValue)) {
        commit('SET_TOP_FILTER', { param: insiderUrlValue, value: true });
      } else if (urlParams.has('top_trades_24h')) {
        commit('SET_TOP_FILTER', { param: 'top_trades_24h', value: true });
      } else if (urlParams.has('top_pnl_24h')) {
        commit('SET_TOP_FILTER', { param: 'top_pnl_24h', value: true });
      } else if (urlParams.has('bet_value')) {
        commit('SET_TOP_FILTER', { param: 'bet_value', value: 10000 });
      }
      if (urlParams.has('user')) {
        commit('SET_ADDITIONAL_FILTER', { type: 'user', value: urlParams.get('user')});
      } else if (urlParams.has('market')) {
        commit('SET_ADDITIONAL_FILTER', { type: 'market', value: urlParams.get('market')});
      }
      if (urlParams.has('category')) {
        const categories = urlParams.get('category').split(',');
        commit('SET_CATEGORIES', categories);
      }
      // fresh_wallet/active_hold применимы только в insider-режиме — если топ-фильтр другой, игнорируем.
      // Дефолты: fresh_wallet=true, active_hold=false. В URL пишем только «не-дефолт».
      const isInsiderUrl = INSIDER_FILTER_PARAMS.includes(urlParams.get('insider'));
      if (urlParams.get('fresh_wallet') === 'false' && isInsiderUrl) {
        commit('SET_FRESH_WALLET', false);
      }
      if (urlParams.get('active_hold') === 'true' && isInsiderUrl) {
        commit('SET_ACTIVE_HOLD', true);
      }
      // Пользовательские пороги инсайдер-фильтра — только в insider-режиме. Невалидные числа игнорируем.
      if (isInsiderUrl) {
        const overrides = {};
        INSIDER_PARAM_NAMES.forEach((name) => {
          const raw = urlParams.get(name);
          if (raw === null) return;
          const num = Number(raw);
          if (Number.isFinite(num)) overrides[name] = num;
        });
        if (Object.keys(overrides).length > 0) commit('PATCH_INSIDER_PARAMS', overrides);
      }
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX



    //======================================================================================
    // BOOKMARKS
    //======================================================================================
    //Загрузка избранных трейдов с сервера
    async loadBookmarks({ commit }) {
      try {
        const data = await apiGetTradesBookmarks();
        commit('SET_BOOKMARKS', data.results || data);
      } catch (error) {
        console.error('Ошибка загрузки избранного:', error);
      }
    },

    //Переключение трейда в/из избранного
    async toggleBookmark({ state, commit }, id) {
      const isBookmarked = state.bookmarksMap.includes(id);
      const trade = state.data[id];
      commit(isBookmarked ? 'MOVE_TO_REGULAR' : 'MOVE_TO_BOOKMARKS', id);
      try {
        await apiToggleTradeBookmark(id, trade.content_type);
      } catch (error) {
        console.error('Ошибка переключения избранного:', error);
        commit(isBookmarked ? 'MOVE_TO_BOOKMARKS' : 'MOVE_TO_REGULAR', id);
      }
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX



    //======================================================================================
    // PAGINATION
    //======================================================================================
    //Загрузка следующей страницы трейдов (пагинация)
    async loadNext({ state, getters, commit }) {
      if (state.isLoadingNext) return;
      if (!state.next) return
      const requestVersion = state.requestVersion;
      commit('SET_IS_LOADING_NEXT', true);
      try {
        const data = await apiGetNextPage(state.next);
        if (!data?.results) {
          console.error('Некорректный ответ API:', data);
          return;
        }
        if (state.requestVersion !== requestVersion) return;
        //В insider-режиме пагинация возвращает список юзеров — сплющиваем в трейды.
        const results = getters.isInsiderMode ? mapInsidersToTrades(data.results) : data.results;
        commit('SET_DATA', results);
        commit('SET_NEXT', data.next);
      } catch (error) {
        console.error('Ошибка загрузки следующей страницы:', error);
      } finally {
        commit('SET_IS_LOADING_NEXT', false);
      }
    },

    //Автообновление трейдов с ограничением по количеству (для polling)
    async autoUpdateTrades({ commit, getters, state }, maxItems = 50) {
      if (state.searchQuery.length > 0) return;
      if (state.isLoadingFilter) return;
      const requestVersion = state.requestVersion;
      try {
        const { results, next } = await fetchTradesList(getters.activeFiltersParams);
        if (state.requestVersion !== requestVersion) return;
        if (state.searchQuery.length > 0) return;
        const url = buildTradesUrl(getters.activeFiltersParams);
        //В insider-режиме latest_trade юзера меняется между поллами — мерж даст дубли и хаос порядка.
        //Делаем полный replace: порядок и состав задаёт бэк.
        if (getters.isInsiderMode) {
          commit('CLEAR_DATA');
          commit('SET_DATA', results);
        } else {
          commit('UPDATE_DATA_WITH_LIMIT', { newTrades: results, maxItems });
        }
        commit('SET_NEXT', next);
        commit('SET_CURRENT_TRADES_URL', url);
      } catch (error) {
        console.error('Ошибка автообновления:', error);
        throw error;
      }
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX



    //======================================================================================
    // FILTERS
    //======================================================================================
    //Установка топ-фильтра (повторный клик отключает фильтр)
    async setTopFilter({ commit, dispatch, state }, { param, value }) {
      const newParam = state.filters.top === param ? null : param;
      commit('SET_TOP_FILTER', { param: newParam, value });
      commit('SET_ADDITIONAL_FILTER', null);
      // Fresh-deposit/active-hold/insider-пороги имеют смысл только в insider-режиме — иначе сбрасываем.
      if (!INSIDER_FILTER_PARAMS.includes(newParam)) {
        commit('SET_FRESH_WALLET', false);
        commit('SET_ACTIVE_HOLD', true);
        commit('RESET_INSIDER_PARAMS');
      }
      await dispatch('applyFilters');
    },

    //Переопределение порогов инсайдерского фильтра из UI (merge с текущими).
    //Значения null/undefined в patch → стереть оверрайд (= использовать дефолт бэка).
    async setInsiderParams({ commit, dispatch }, patch) {
      commit('PATCH_INSIDER_PARAMS', patch);
      await dispatch('applyFilters');
    },

    //Сброс всех пользовательских оверрайдов инсайдер-фильтра к дефолтам.
    async resetInsiderParams({ commit, dispatch }) {
      commit('RESET_INSIDER_PARAMS');
      await dispatch('applyFilters');
    },

    //Переключение fresh-deposit фильтра (работает только в insider-режиме)
    async toggleFreshWallet({ commit, dispatch, state }) {
      commit('SET_FRESH_WALLET', !state.filters.freshWallet);
      await dispatch('applyFilters');
    },

    //Переключение active-hold фильтра (работает только в insider-режиме)
    async toggleActiveHold({ commit, dispatch, state }) {
      commit('SET_ACTIVE_HOLD', !state.filters.activeHold);
      await dispatch('applyFilters');
    },

    //Установка дополнительного фильтра по юзеру/маркету (повторный клик отключает фильтр)
    async setAdditionalFilter({ commit, dispatch, state }, { type, value }) {
      const additional = state.filters.additional;
      const isSame = additional?.type === type && additional?.value === value;
      const newValue = isSame ? null : { type, value };
      commit('SET_ADDITIONAL_FILTER', newValue);
      commit('SET_TOP_FILTER', { param: null, value: null });
      await dispatch('applyFilters');
    },

    //Переключение категории в фильтре (добавить/удалить)
    async toggleCategory({ commit, dispatch, state }, categoryName) {
      const categories = [...state.filters.categories];
      const index = categories.indexOf(categoryName);
      (index > -1) ? categories.splice(index, 1) : categories.push(categoryName);
      commit('SET_CATEGORIES', categories);
      saveCategories(categories);
      await dispatch('applyFilters');
    },

    //Применение текущих фильтров: очистка данных и загрузка новых
    async applyFilters({ commit, getters, state }) {
      commit('ABORT_CURRENT_REQUEST');
      commit('INCREMENT_REQUEST_VERSION');
      const requestVersion = state.requestVersion;
      const abortController = new AbortController();
      commit('SET_ABORT_CONTROLLER', abortController);
      commit('SET_LOADING_FILTER', true);
      commit('SET_SEARCH_QUERY', '');
      try {
        const { results, next } = await fetchTradesList(getters.activeFiltersParams, abortController.signal);
        const url = buildTradesUrl(getters.activeFiltersParams);
        if (state.requestVersion !== requestVersion) return;
        commit('CLEAR_DATA');
        commit('SET_DATA', results);
        commit('SET_NEXT', next);
        commit('SET_CURRENT_TRADES_URL', url);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Ошибка применения фильтров:', error);
      } finally {
        if (state.requestVersion === requestVersion) {
          commit('SET_LOADING_FILTER', false);
          commit('SET_ABORT_CONTROLLER', null);
        }
      }
    },

    //Очистка дополнительного фильтра (БЕЗ сброса категорий)
    async clearAdditionalFilter({ commit, dispatch }) {
      commit('SET_ADDITIONAL_FILTER', null);
      await dispatch('applyFilters');
    },

    //Очистка топ-фильтра (БЕЗ сброса категорий)
    async clearTopFilter({ commit, dispatch }) {
      commit('SET_TOP_FILTER', { param: null, value: null });
      await dispatch('applyFilters');
    },

    //Очистка и топ и дополнительного фильтра (БЕЗ сброса категорий)
    async clearTopAndAdditionalFilter({ commit, dispatch }) {
      commit('SET_ADDITIONAL_FILTER', null);
      commit('SET_TOP_FILTER', { param: null, value: null });
      await dispatch('applyFilters');
    },

    //Сброс всех фильтров к дефолтным значениям
    async resetAllFilters({ commit, dispatch }) {
      commit('SET_TOP_FILTER', { param: null, value: null });
      commit('SET_ADDITIONAL_FILTER', null);
      commit('SET_CATEGORIES', getInitialCategories());
      commit('SET_FRESH_WALLET', false);
      commit('SET_ACTIVE_HOLD', true);
      commit('RESET_INSIDER_PARAMS');
      await dispatch('applyFilters');
    },

    //Сброс категорий к дефолтным (без сброса других фильтров)
    async resetCategories({ commit, dispatch }) {
      commit('SET_CATEGORIES', getInitialCategories());
      await dispatch('applyFilters');
    },

    //Полный сброс стора в исходное состояние
    async resetStore({ commit }) {
      commit('RESET_STORE');
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX


    
    //======================================================================================
    // SEARCH
    //======================================================================================
    //Поиск трейдов 
    async searchTrades({ commit, state }, query) {
      if (!query || query.length < 2) {
        commit('SET_SEARCH_QUERY', '');
        return;
      }
      
      commit('ABORT_CURRENT_REQUEST');
      commit('INCREMENT_REQUEST_VERSION');
      const requestVersion = state.requestVersion;
      const abortController = new AbortController();
      commit('SET_ABORT_CONTROLLER', abortController);
      commit('SET_LOADING_FILTER', true);
      commit('SET_SEARCH_QUERY', query);
      
      try {
        const data = await apiSearchTrades(query, abortController.signal);
        commit('SET_TOP_FILTER', { param: null, value: null });
        commit('SET_ADDITIONAL_FILTER', null);
        if (state.requestVersion !== requestVersion) return;
        commit('CLEAR_DATA');
        commit('SET_DATA', data.results || data);
        commit('SET_NEXT', data.next);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Ошибка поиска:', error);
      } finally {
        if (state.requestVersion === requestVersion) {
          commit('SET_LOADING_FILTER', false);
          commit('SET_ABORT_CONTROLLER', null);
        }
      }
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
  },

  mutations: {
    //======================================================================================
    // DATA
    //======================================================================================
    //Добавление новых трейдов в стор
    SET_DATA(state, trades) {
      const bookmarksSet = new Set(state.bookmarksMap);
      const mapSet = new Set(state.map);
      
      trades.forEach((trade) => {
        const exists = state.data[trade.id];
        
        if (!exists) {
          state.data[trade.id] = trade;
          if (!bookmarksSet.has(trade.id)) {
            state.map.push(trade.id);
            mapSet.add(trade.id);
          }
        } else {
          Object.assign(state.data[trade.id], trade);
        }
      });
      
      sortMapByFilter(state);
    },

    //Установка избранных трейдов из API
    SET_BOOKMARKS(state, bookmarks) {
      const bookmarksSet = new Set(state.bookmarksMap);
      bookmarks.forEach((trade) => {
        state.data[trade.id] = { ...(state.data[trade.id] || {}), ...trade };
        if (!bookmarksSet.has(trade.id)) state.bookmarksMap.push(trade.id);
        const mapIndex = state.map.indexOf(trade.id);
        if (mapIndex > -1) state.map.splice(mapIndex, 1);
      });
      state.isBookmarksLoaded = true;
      updateBookmarksCount(state);
    },

    //Перемещение трейда из обычного списка в избранное
    MOVE_TO_BOOKMARKS(state, tradeId) {
      const mapIndex = state.map.indexOf(tradeId);
      if (mapIndex > -1) state.map.splice(mapIndex, 1);
      if (!state.bookmarksMap.includes(tradeId)) state.bookmarksMap.push(tradeId);
      updateBookmarksCount(state);
    },

    //Перемещение трейда из избранного в обычный список
    MOVE_TO_REGULAR(state, tradeId) {
      const bookmarkIndex = state.bookmarksMap.indexOf(tradeId);
      if (bookmarkIndex > -1) state.bookmarksMap.splice(bookmarkIndex, 1);
      if (!state.map.includes(tradeId)) state.map.push(tradeId);
      updateBookmarksCount(state);
    },

    //Обновление данных с лимитом: добавляет новые трейды, сортирует, удаляет лишние
    UPDATE_DATA_WITH_LIMIT(state, { newTrades, maxItems }) {
      const bookmarksSet = new Set(state.bookmarksMap);
      const mapSet = new Set(state.map);
      const newIds = [];
      newTrades.forEach((trade) => {
        state.data[trade.id] = { ...(state.data[trade.id] || {}), ...trade };
        if (!mapSet.has(trade.id) && !bookmarksSet.has(trade.id)) newIds.push(trade.id);
      });
      if (newIds.length > 0) state.map = [...newIds, ...state.map];
      sortMapByFilter(state);
      if (state.map.length > maxItems) {
        const toRemove = state.map.slice(maxItems);
        state.map = state.map.slice(0, maxItems);
        toRemove.forEach(id => {
          if (!bookmarksSet.has(id)) delete state.data[id];
        });
      }
    },

    //Очистка обычных трейдов (сохраняет избранное)
    CLEAR_DATA(state) {
      const bookmarksSet = new Set(state.bookmarksMap);
      const bookmarkedData = {};
      bookmarksSet.forEach(id => {
        if (state.data[id]) bookmarkedData[id] = state.data[id];
      });
      state.data = bookmarkedData;
      state.map = [];
      state.next = null;
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX



    //======================================================================================
    // FLAGS
    //======================================================================================
    //Установка флага готовности данных
    SET_READY_FLAG(state, status) { state.isReady = status; }, 

    //Установка флага загрузки следующей страницы
    SET_IS_LOADING_NEXT(state, status) { state.isLoadingNext = status; }, 

    //Установка флага применения фильтров
    SET_LOADING_FILTER(state, status) { state.isLoadingFilter = status; }, 

    //Установка URL следующей страницы для пагинации
    SET_NEXT(state, nextUrl) { state.next = nextUrl; }, 
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX



    //======================================================================================
    // FILTERS
    //======================================================================================
    //Установка топ-фильтра и его значения
    SET_TOP_FILTER(state, { param, value }) { 
      state.filters.top = param; 
      state.filters.topValue = value; 
    },

    //Установка дополнительного фильтра (user/market)
    SET_ADDITIONAL_FILTER(state, filter) { state.filters.additional = filter; }, 

    //Установка выбранных категорий и сохранение в localStorage
    SET_CATEGORIES(state, categories) {
      state.filters.categories = categories;
      localStorage.setItem(CATEGORIES_STORAGE_KEY, JSON.stringify(categories));
    },

    //Установка флага fresh-deposit фильтра
    SET_FRESH_WALLET(state, value) { state.filters.freshWallet = Boolean(value); },

    //Установка флага active-hold фильтра
    SET_ACTIVE_HOLD(state, value) { state.filters.activeHold = Boolean(value); },

    //Патч пользовательских порогов инсайдер-фильтра: merge с текущими.
    //Значения null/undefined/NaN в patch → стираем оверрайд (возвращаемся к дефолту бэка).
    PATCH_INSIDER_PARAMS(state, patch) {
      const next = { ...state.filters.insiderParams };
      Object.entries(patch).forEach(([key, value]) => {
        if (!INSIDER_PARAM_NAMES.includes(key)) return;
        next[key] = (value === null || value === undefined || Number.isNaN(value)) ? null : Number(value);
      });
      state.filters.insiderParams = next;
    },

    //Сброс всех пользовательских оверрайдов инсайдер-фильтра.
    RESET_INSIDER_PARAMS(state) {
      state.filters.insiderParams = emptyInsiderParams();
    },

    //Сохранение текущего URL трейдов
    SET_CURRENT_TRADES_URL(state, url) { state.currentTradesUrl = url; },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX



    //======================================================================================
    // SEARCH
    //======================================================================================
    //Сохранение поискового запроса
    SET_SEARCH_QUERY(state, query) {
      state.searchQuery = query;
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX



    //======================================================================================
    // REQUEST CONTROL
    //======================================================================================
    //Инкремент версии запроса (каждый новый запрос получает новую версию)
    INCREMENT_REQUEST_VERSION(state) {
      state.requestVersion += 1;
    },

    //Сохранение AbortController
    SET_ABORT_CONTROLLER(state, controller) {
      state.abortController = controller;
    },

    //Отмена текущего запроса
    ABORT_CURRENT_REQUEST(state) {
      if (state.abortController) {
        state.abortController.abort();
        state.abortController = null;
      }
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX


    //======================================================================================
    // MEMORY MANAGEMENT
    //======================================================================================
    //Очистка устаревших данных из state.data
    CLEANUP_OLD_DATA(state) {
      const bookmarksSet = new Set(state.bookmarksMap);
      const mapSet = new Set(state.map);
      
      // Удаляем из data всё, что не в map и не в bookmarks
      Object.keys(state.data).forEach(id => {
        if (!mapSet.has(id) && !bookmarksSet.has(id)) {
          delete state.data[id];
        }
      });
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
    

    //======================================================================================
    // ОБНУЛЕНИЕ (ВОЗВРАЩЕНИЕ В ИСХОДНОЕ СОСТОЯНИЕ)
    //======================================================================================
    //Полный сброс стора к начальному состоянию
    RESET_STORE(state) {
      if (state.abortController) {
        state.abortController.abort();
      }
      state.data = {};
      state.map = [];
      state.bookmarksMap = [];
      state.next = null;
      state.isReady = false;
      state.isLoadingNext = false;
      state.isLoadingFilter = false;
      state.isBookmarksLoaded = false;
      state.filters.top = null;
      state.filters.topValue = null;
      state.filters.additional = null;
      state.filters.categories = getInitialCategories();
      state.filters.freshWallet = true;
      state.filters.activeHold = false;
      state.filters.insiderParams = emptyInsiderParams();
      state.currentTradesUrl = null;
      state.searchQuery = '';
      state.bookmarksCount = getInitialBookmarksCount();
      state.requestVersion = 0;
      state.abortController = null;
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
  },

  getters: {
    //======================================================================================
    // КЕШИРОВАННЫЕ SET (только для использования в контексте store)
    //======================================================================================
    //Set избранных ID для быстрой проверки
    _bookmarksSet: (state) => new Set(state.bookmarksMap), 

    //Set категорий для быстрой проверки
    _categoriesSet: (state) => new Set(state.filters.categories), 
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX



    //======================================================================================
    // DATA
    //======================================================================================
    //Объект всех трейдов по ID
    allTrades: (state) => state.data, 
    
    //Получить трейд по ID
    tradeById: (state) => (id) => state.data[id] || null, 

    //Массив ID обычных трейдов (уже отсортирован)
    tradesMap: (state) => state.map, 

    //Массив ID избранных трейдов
    bookmarksMap: (state) => state.bookmarksMap, 

    //Массив объектов избранных трейдов
    bookmarkedTrades: (state) => state.bookmarksMap.map(id => state.data[id]).filter(Boolean), 

    //Проверка, находится ли трейд в избранном
    isBookmarked: (state, getters) => (tradeId) => getters._bookmarksSet.has(tradeId),
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX



    //======================================================================================
    // STATUS
    //======================================================================================
    //Готовы ли данные (завершена ли инициализация)
    isReady: (state) => state.isReady, 

    //Идет ли загрузка следующей страницы
    isLoadingNext: (state) => state.isLoadingNext, 

    //Идет ли применение фильтров
    isLoadingFilter: (state) => state.isLoadingFilter, 

    //Загружены ли избранные
    isBookmarksLoaded: (state) => state.isBookmarksLoaded, 

    //URL следующей страницы (null если конец)
    nextUrl: (state) => state.next, 
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX



    //======================================================================================
    // SEARCH
    //======================================================================================
    //Текущий поисковый запрос
    searchQuery: (state) => state.searchQuery,

    //Активен ли поиск (есть ли запрос)
    isSearchActive: (state) => state.searchQuery.length > 0,
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX

    
    
    //======================================================================================
    // FILTERS
    //======================================================================================
    //Активный топ-фильтр (название параметра)
    activeTopFilter: (state) => state.filters.top, 

    //Активный дополнительный фильтр (объект с type и value)
    activeAdditionalFilter: (state) => state.filters.additional, 

    //Массив активных категорий
    activeCategories: (state) => state.filters.categories, 
    
    //Проверка, активна ли категория
    isCategoryActive: (state, getters) => (categoryName) => getters._categoriesSet.has(categoryName),

    //Есть ли фильтр по маркету
    hasActiveMarketFilter: (state) => state.filters.additional?.type === 'market', 

    //Есть ли фильтр по юзеру
    hasActiveUserFilter: (state) => state.filters.additional?.type === 'user', 

    //Текущий URL трейдов
    getCurrentTradesUrl: (state) => state.currentTradesUrl, 
    
    //Есть ли хоть какие-то активные фильтры
    hasActiveFilters: (state) => {
      const hasNonDefaultCategories = state.filters.categories.length !== DEFAULT_CATEGORIES.length ||
        !state.filters.categories.every(cat => DEFAULT_CATEGORIES.includes(cat));
      return state.filters.top !== null || state.filters.additional !== null || hasNonDefaultCategories;
    },

    //Активен ли режим инсайдеров (единственный оставшийся top-filter 'insider')
    isInsiderMode: (state) => INSIDER_FILTER_PARAMS.includes(state.filters.top),

    //Активен ли fresh-deposit фильтр (имеет смысл только в insider-режиме)
    isFreshWallet: (state) => state.filters.freshWallet,

    //Активен ли active-hold фильтр (имеет смысл только в insider-режиме)
    isActiveHold: (state) => state.filters.activeHold,

    //Текущие значения пользовательских оверрайдов порогов (null = использовать дефолт).
    insiderParams: (state) => state.filters.insiderParams,

    //Есть ли хоть один переопределённый порог (для индикации активности «настроек»).
    hasInsiderParamOverrides: (state) =>
      INSIDER_PARAM_NAMES.some((name) => state.filters.insiderParams[name] !== null),

    //Параметры для API запроса на основе активных фильтров
    activeFiltersParams: (state, getters) => {
      const params = {};

      //Insider-режим: additional игнорируем, категории пропускаем через "insider-allowed" фильтр.
      if (getters.isInsiderMode) {
        params.insider_mode = true;
        // Дефолты: fresh_wallet=true, active_hold=false. Шлём только не-дефолтные значения.
        if (!state.filters.freshWallet) params.fresh_wallet = false;
        if (state.filters.activeHold) params.active_hold = true;
        // Отбрасываем sports/pop-culture из общего списка категорий — бэк всё равно их исключает.
        const insiderCategories = state.filters.categories.filter(
          (c) => !INSIDER_EXCLUDED_CATEGORY_SLUGS.includes(c),
        );
        // Если юзер не сузил выбор — не шлём category, чтобы бэк пошёл быстрым путём (без
        // коррелированного EXISTS с M2M-join категорий). Любое сужение (включая «всё снято»)
        // отсылаем явно: empty = «ничего не показывать», частичный набор = «только эти».
        const insiderAllowedDefault = DEFAULT_CATEGORIES.filter(
          (c) => !INSIDER_EXCLUDED_CATEGORY_SLUGS.includes(c),
        );
        const isAllInsiderAllowedSelected =
          insiderCategories.length === insiderAllowedDefault.length &&
          insiderAllowedDefault.every((c) => insiderCategories.includes(c));
        if (!isAllInsiderAllowedSelected) {
          params.category = insiderCategories.join(',');
        }
        // Пользовательские оверрайды порогов: шлём только непустые, чтобы бэк применил дефолты к остальным.
        const overrides = {};
        INSIDER_PARAM_NAMES.forEach((name) => {
          const val = state.filters.insiderParams[name];
          if (val !== null && val !== undefined) overrides[name] = val;
        });
        if (Object.keys(overrides).length > 0) params.insider_params = overrides;
        return params;
      }

      if (state.filters.top) {
        params[state.filters.top] = state.filters.topValue;
      }

      // Drill-down по кошельку/маркету — категории сбрасываем: бэк иначе пересечёт и часто вернёт пусто
      // (например, user с трейдами в politics при активной категории crypto).
      if (state.filters.additional) {
        const apiKey = state.filters.additional.type === 'user' ? 'user' : 'market';
        params[apiKey] = state.filters.additional.value;
        return params;
      }

      // Отправляем category всегда (даже пустым), чтобы отличить «все сняты» от «не фильтруем»
      params.category = state.filters.categories.join(',');

      return params;
    },

    //Находятся ли фильтры в дефолтном состоянии
    isDefaultFilters: (state) => {
      const noTopOrAdditional = state.filters.top === null && state.filters.additional === null;
      const currentCategories = state.filters.categories;
      const categoriesMatch = 
        currentCategories.length === DEFAULT_CATEGORIES.length &&
        currentCategories.every(cat => DEFAULT_CATEGORIES.includes(cat));
      
      return noTopOrAdditional && categoriesMatch;
    },

    //Количество избранных (из localStorage для резервирования места)
    bookmarksCount: (state) => state.bookmarksCount, 
  }
};

export default Polymarket;